/**
 * Producer Training Clip 1 — The Producer Interface
 * Comprehensive walkthrough highlighting every section:
 *   1. CompetitionView — OBS overlay URL, Producer card
 *   2. Producer page — header (LIVE/OFF badge, heat dropdown, Edit Heats, Go Live)
 *   3. Overlay controls (Show Heat Name, Leaderboard, Riders, Scores, Clear Focus)
 *   4. Overlay style switcher
 *   5. Rider cards (name, nickname, attempt dots, judge scores, Focus/Scores buttons)
 *   6. Notifications panel
 *   7. OBS overlay preview (bottom-right callout)
 * 900 frames at 30fps = 30 seconds.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../components/AutoCursor";
import { ScreenMock } from "../components/ScreenMock";
import { FadeTransition } from "../components/FadeTransition";
import { StepLabel } from "../components/StepLabel";

export function ProducerCourse1() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={60}><Title /></Sequence>
      <Sequence from={50} durationInFrames={160}><SceneCompView /></Sequence>
      <Sequence from={200} durationInFrames={1100}>
        <FadeTransition durationInFrames={1100} fadeIn={10} fadeOut={10}><SceneProducer /></FadeTransition>
      </Sequence>
      <Sequence from={1295} durationInFrames={65}><Done /></Sequence>
    </AbsoluteFill>
  );
}

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 44, 60], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: "#a855f7", textTransform: "uppercase", letterSpacing: 3, fontFamily: M, marginBottom: 14 }}>Producer Training · Clip 1</div>
      <div style={{ fontSize: 48, fontWeight: 800, color: "#fff" }}>The Producer Interface</div>
    </AbsoluteFill>
  );
}

/* ── Scene 1: CompetitionView — OBS URL + Producer card ── */

function SceneCompView() {
  const frame = useCurrentFrame();
  const urlCopied = frame >= 55;

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock">
        <div style={shell}>
          {/* Nav bar */}
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ ...navLink, color: "#e4e4e7" }}>COMPETITIONS</span>
            <span style={navLink}>RIDERS</span>
            <span style={navLink}>TRAINING</span>
            <span style={navLink}>SUPPORT</span>
            <span style={{ marginLeft: "auto", ...mono, fontSize: 11, color: "#71717a" }}>Takoda Mundy</span>
            <span style={{ fontSize: 11, color: "#52525b", marginLeft: 12, textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.5 }}>Sign Out</span>
          </div>
          <div style={{ padding: "24px 32px" }}>
            {/* Page header — matches real CompetitionView */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 24, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>BOARDSTOCK</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                <span style={{ fontSize: 14, color: "#71717a" }}>Competition</span>
                <span style={{ ...statusBadge, background: "#ef4444", color: "#fff" }}>LIVE</span>
              </div>
              <div style={{ marginTop: 6 }}>
                <span style={{ ...badge, background: "#3b82f6" }}>Admin</span>
              </div>
            </div>

            {/* 3-column cards: Setup, Judge, Producer */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
              <div style={cardStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>Setup</div>
                <div style={{ fontSize: 12, color: "#71717a", marginTop: 6 }}>Configure divisions, heats, assign riders, and invite your team.</div>
                <div style={{ marginTop: 10 }}><span style={{ ...badge, background: "#3b82f6" }}>Admin</span></div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>Judge</div>
                <div style={{ fontSize: 12, color: "#71717a", marginTop: 6 }}>Score riders in real-time with 0.1 precision.</div>
                <div style={{ marginTop: 10 }}><span style={{ ...badge, background: "#22c55e" }}>Access</span></div>
              </div>
              <div id="comp-producer-card" style={cardStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>Producer</div>
                <div style={{ fontSize: 12, color: "#71717a", marginTop: 6 }}>Control the live overlay. Focus riders, show scores, leaderboard. Go live or dark.</div>
                <div style={{ marginTop: 10 }}><span style={{ ...badge, background: "#a855f7" }}>Access</span></div>
              </div>
            </div>

            {/* OBS Overlay — separate card below grid */}
            <div style={{ ...cardStyle, marginBottom: 24, maxWidth: 480 }}>
              <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>OBS Overlay</div>
              <div style={{ fontSize: 12, color: "#71717a", marginTop: 6 }}>Add this URL as a Browser Source in OBS (1920x1080). No login required.</div>
              <div style={{ marginTop: 12 }}>
                <code id="obs-url" style={{ display: "block", padding: "10px 12px", background: "#0a0a0b", border: "1px solid #2a2a32", ...mono, fontSize: 11, color: "#a1a1aa", wordBreak: "break-all" as const }}>
                  https://raley.koda-software.com/obs/262b6dda1e344cad9...
                </code>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <div style={{ padding: "4px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", background: "#1a1a1e", color: "#e4e4e7", border: "1px solid #2a2a32" }}>Open</div>
                  <div id="copy-url-btn" style={{ padding: "4px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: urlCopied ? "#22c55e" : "#71717a", cursor: "pointer" }}>
                    {urlCopied ? "URL copied to clipboard" : "Copy URL"}
                  </div>
                </div>
              </div>
            </div>

            {/* Team table */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Team (4)</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #2a2a32" }}>
                    {["Name", "Email", "Roles", "Judge #"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, color: "#52525b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, ...mono }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: "Takoda Mundy", email: "takamundy@gmail.com", roles: ["admin"], judgeNum: null },
                    { name: "Sarah Chen", email: "sarah@example.com", roles: ["judge"], judgeNum: 1 },
                    { name: "Mike Torres", email: "mike@example.com", roles: ["judge"], judgeNum: 2 },
                    { name: "Lily Park", email: "lily@example.com", roles: ["judge"], judgeNum: 3 },
                  ].map((m) => (
                    <tr key={m.email} style={{ borderBottom: "1px solid #1a1a1e" }}>
                      <td style={{ padding: "8px 10px", fontWeight: 500 }}>{m.name}</td>
                      <td style={{ padding: "8px 10px", ...mono, fontSize: 11, color: "#71717a" }}>{m.email}</td>
                      <td style={{ padding: "8px 10px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          {m.roles.map((r) => (
                            <span key={r} style={{ ...badge, background: r === "admin" ? "#3b82f6" : "#22c55e" }}>{r}</span>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: "8px 10px", ...mono, color: "#71717a" }}>{m.judgeNum ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <AutoCursor points={[
          { x: 960, y: 400, frame: 0 },
          { target: "obs-url", frame: 20 },
          { target: "obs-url", frame: 35 },
          { target: "copy-url-btn", frame: 42 },
          { target: "copy-url-btn", frame: 55, click: true },
          { x: 960, y: 400, frame: 80 },
          { target: "comp-producer-card", frame: 100 },
          { target: "comp-producer-card", frame: 130, click: true },
          { x: 960, y: 400, frame: 155 },
        ]} />
      </ScreenMock>
      <StepLabel text="Copy the OBS overlay URL — paste it as a Browser Source in OBS" show={5} hide={70} color="#a855f7" />
      <StepLabel text="Click Producer to open the control panel" show={85} hide={155} color="#a855f7" />
    </AbsoluteFill>
  );
}

/* ── Scene 2: Producer page — section-by-section walkthrough ── */

function SceneProducer() {
  const frame = useCurrentFrame();

  /*
   * Timeline (frames relative to this sequence, ~5s per section):
   *   0-150:     Header: LIVE/OFF badge, heat dropdown, Edit Heats, Go Live/Dark
   *   150-300:   Edit Heats modal
   *   300-450:   Overlay controls (show/hide toggles)
   *   450-600:   Overlay style switcher
   *   600-780:   Rider cards (scores, attempts, focus/scores)
   *   780-930:   Notifications
   *   930-1050:  OBS overlay preview
   *   1050-1090: Full view
   */

  const section =
    frame < 150 ? "header" :
    frame < 300 ? "edit-heats" :
    frame < 450 ? "controls" :
    frame < 600 ? "styles" :
    frame < 780 ? "riders" :
    frame < 930 ? "notifications" :
    frame < 1090 ? "obs-preview" :
    "none";

  const showEditModal = section === "edit-heats";

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock/producer">
        <div style={shell}>
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ ...navLink, color: "#e4e4e7" }}>Competitions</span>
            <span style={navLink}>Riders</span>
            <span style={{ marginLeft: "auto", ...mono, fontSize: 11, color: "#71717a" }}>producer@koda-software.com</span>
          </div>
          <div style={{ padding: "12px 24px", overflow: "hidden" }}>
            {/* ── Header — matches page-header structure ── */}
            <div id="prod-header" style={{ marginBottom: 14, padding: 8, border: "1px solid transparent", ...glow(section === "header") }}>
              <div style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Sarah</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 2 }}>
                <span style={{ fontSize: 13, color: "#71717a" }}>Boardstock Invitational</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                <span style={{ ...statusBadge, background: "#ef4444", color: "#fff" }}>LIVE</span>
                <div id="heat-dropdown" style={{ padding: "6px 12px", fontSize: 14, background: "#111113", border: "1px solid #2a2a32", borderRadius: 6, color: "#e4e4e7", minWidth: 180, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Semi-Finals</span>
                  <span style={{ fontSize: 10, color: "#52525b" }}>&#9662;</span>
                </div>
                <div id="edit-heats-btn" style={{ padding: "4px 10px", fontSize: 11, fontWeight: 600, background: "#1a1a1e", color: "#e4e4e7", border: "1px solid #2a2a32" }}>Edit Heats</div>
                <div id="go-live-btn" style={{ padding: "4px 10px", fontSize: 11, fontWeight: 600, background: "transparent", color: "#ef4444", border: "1px solid #ef4444" }}>Go Dark</div>
              </div>
            </div>

            {/* ── Overlay controls ── */}
            <div id="prod-controls" style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" as const, padding: 6, border: "1px solid transparent", ...glow(section === "controls") }}>
              {[
                { label: "Hide Heat Name", on: true },
                { label: "Show Heat Leaderboard", on: false },
                { label: "Hide Heat Riders", on: true },
                { label: "Show Scores", on: false },
                { label: "Clear Focus", on: false },
              ].map(({ label, on }) => (
                <div key={label} style={{ padding: "3px 8px", fontSize: 9, fontWeight: 600, background: on ? "#3b82f6" : "#1a1a1e", color: on ? "#fff" : "#e4e4e7", border: `1px solid ${on ? "#3b82f6" : "#2a2a32"}` }}>{label}</div>
              ))}
            </div>

            {/* ── Overlay style ── */}
            <div id="prod-styles" style={{ marginBottom: 12, padding: 6, border: "1px solid transparent", ...glow(section === "styles") }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: "#71717a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4, ...mono }}>Overlay Style</div>
              <div style={{ display: "flex", gap: 4 }}>
                {["Steel", "Olympic", "Broadcast", "Neon", "Clean"].map((s, i) => (
                  <div key={s} style={{ padding: "3px 8px", fontSize: 9, fontWeight: 600, background: i === 0 ? "#3b82f6" : "#1a1a1e", color: i === 0 ? "#fff" : "#71717a", border: `1px solid ${i === 0 ? "#3b82f6" : "#2a2a32"}` }}>{s}</div>
                ))}
              </div>
            </div>

            {/* ── Rider cards ── */}
            <div id="prod-riders" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, padding: 6, border: "1px solid transparent", ...glow(section === "riders") }}>
              {RIDERS.map((r) => {
                const validAvgs = r.attempts.filter((v): v is number => v !== null);
                const bestAvg = validAvgs.length > 0 ? Math.max(...validAvgs) : null;
                return (
                <div key={r.name} style={{ background: "#111113", border: "1px solid #2a2a32", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: "#71717a", ...mono }}>{r.nick}</div>

                  {/* Per-attempt average boxes (best highlighted) */}
                  <div style={{ display: "flex", gap: 3, marginTop: 6, alignItems: "center" }}>
                    {r.attempts.map((avg, j) => {
                      const isBest = avg !== null && bestAvg !== null && avg === bestAvg;
                      return (
                        <div key={j} style={{
                          background: isBest ? "#3b82f6" : "#0a0a0b",
                          border: `1px solid ${isBest ? "#3b82f6" : "#2a2a32"}`,
                          borderRadius: 3, padding: "1px 5px", fontSize: 9, ...mono,
                          color: isBest ? "#fff" : avg != null ? "#e4e4e7" : "#52525b",
                          fontWeight: isBest ? 700 : 400, minWidth: 24, textAlign: "center" as const,
                        }}>
                          {avg != null ? avg.toFixed(1) : "-"}
                        </div>
                      );
                    })}
                    <div style={{ fontSize: 10, fontWeight: 700, ...mono, color: r.final != null ? "#3b82f6" : "#52525b", marginLeft: 2 }}>
                      {r.final != null ? r.final.toFixed(1) : "-"}
                    </div>
                  </div>

                  {/* Attempt dots with -/+ controls and counter */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                    <span style={{ fontSize: 9, color: "#52525b", cursor: "pointer" }}>-</span>
                    <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                      {r.attempts.map((_, i) => {
                        const active = i + 1 <= r.currentAttempt;
                        const isCurrent = i + 1 === r.currentAttempt;
                        return <div key={i} style={{
                          width: 7, height: 7, borderRadius: "50%",
                          background: active ? "#3b82f6" : "transparent",
                          border: `1px solid ${active ? "#3b82f6" : "#52525b"}`,
                          boxShadow: isCurrent ? "0 0 0 2px #3b82f6" : "none",
                          opacity: active ? 1 : 0.3,
                        }} />;
                      })}
                    </div>
                    <span style={{ fontSize: 9, color: "#52525b", cursor: "pointer" }}>+</span>
                    <span style={{ fontSize: 9, color: "#52525b", ...mono }}>{r.currentAttempt}/3</span>
                  </div>

                  {/* Focus / Scores buttons */}
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <div style={{ padding: "2px 8px", fontSize: 8, fontWeight: 600, textTransform: "uppercase", color: "#71717a", border: "1px solid #2a2a32", background: "#1a1a1e" }}>Focus</div>
                    <div style={{ padding: "2px 8px", fontSize: 8, fontWeight: 600, textTransform: "uppercase", color: "#71717a", border: "1px solid #2a2a32", background: "#1a1a1e" }}>Scores</div>
                  </div>

                  {/* Score grid: judges × attempts */}
                  <div style={{ marginTop: 6, borderTop: "1px solid #2a2a32", paddingTop: 6 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 8, ...mono }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "1px 3px", color: "#52525b", fontWeight: 600 }} />
                          {r.attempts.map((_, a) => (
                            <th key={a} style={{ textAlign: "center", padding: "1px 3px", color: "#52525b", fontWeight: 600 }}>A{a + 1}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {r.judgeScores.map((row, j) => (
                          <tr key={j}>
                            <td style={{ padding: "1px 3px", color: "#52525b", fontWeight: 600 }}>{r.judgeNames[j]}</td>
                            {row.map((v, a) => (
                              <td key={a} style={{ textAlign: "center", padding: "1px 3px", color: v != null ? "#e4e4e7" : "#52525b" }}>
                                {v != null ? v.toFixed(1) : "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr style={{ borderTop: "1px solid #2a2a32" }}>
                          <td style={{ padding: "2px 3px", fontWeight: 700, color: "#52525b" }}>Final</td>
                          {r.attempts.map((avg, a) => (
                            <td key={a} style={{ textAlign: "center", padding: "2px 3px", fontWeight: 700, color: avg != null ? "#3b82f6" : "#52525b" }}>
                              {avg != null ? avg.toFixed(1) : "-"}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                    {r.final != null && (
                      <div style={{ marginTop: 3, fontSize: 9, fontWeight: 700, color: "#3b82f6", ...mono }}>
                        Overall: {r.final.toFixed(1)} (max)
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            {/* ── Notifications (bottom-right) ── */}
            {section === "notifications" && (
              <div id="prod-notifications" style={{
                position: "absolute", top: 60, right: 24,
                background: "#1a1a1e", border: "1px solid #2a2a32", padding: "8px 14px",
                display: "flex", alignItems: "center", gap: 10,
                boxShadow: "0 0 0 2px #22c55e, 0 4px 20px rgba(0,0,0,0.5)",
              }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff", ...mono }}>J1</div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600 }}>scored Harley Clifford #2</div>
                  <div style={{ fontSize: 9, color: "#71717a", ...mono }}>8.5</div>
                </div>
              </div>
            )}
          </div>

          {/* ── Edit Heats modal overlay ── */}
          {showEditModal && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
              <div id="edit-heats-modal" style={{ background: "#111113", border: "1px solid #2a2a32", borderRadius: 8, width: 380, padding: 0, ...glow(true) }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #2a2a32", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Edit Heats</div>
                  <span style={{ color: "#71717a", fontSize: 16, cursor: "pointer" }}>&times;</span>
                </div>
                <div style={{ padding: "12px 16px" }}>
                  <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, fontWeight: 600, color: "#52525b", ...mono }}>MEN&apos;S OPEN</div>
                  {["Round 1", "Semi-Finals", "Finals"].map((h, i) => (
                    <div key={h} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #1a1a1e" }}>
                      <span style={{ fontSize: 12, fontWeight: i === 1 ? 700 : 500, color: i === 1 ? "#3b82f6" : "#e4e4e7" }}>{h}</span>
                      <span style={{ fontSize: 9, color: "#52525b", ...mono }}>3 attempts</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 12, marginBottom: 4, fontWeight: 600, color: "#52525b", ...mono }}>WOMEN&apos;S OPEN</div>
                  {["Round 1", "Finals"].map((h) => (
                    <div key={h} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #1a1a1e" }}>
                      <span style={{ fontSize: 12, color: "#e4e4e7" }}>{h}</span>
                      <span style={{ fontSize: 9, color: "#52525b", ...mono }}>3 attempts</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── OBS preview callout ── */}
        {section === "obs-preview" && (
          <div style={{
            position: "absolute", bottom: 20, right: 20,
            background: "#111113", border: "2px solid #a855f7", borderRadius: 8,
            padding: 10, width: 320, boxShadow: "0 0 0 2px #a855f7, 0 0 20px rgba(168,85,247,0.2)",
          }}>
            <div style={{ fontSize: 9, color: "#a855f7", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, ...mono }}>OBS Overlay Preview</div>
            <div style={{ background: "#0a0a0b", border: "1px solid #2a2a32", height: 160, borderRadius: 4, position: "relative", overflow: "hidden" }}>
              {/* Mini overlay mock */}
              <div style={{ position: "absolute", top: 8, left: 10, display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,0.7)", padding: "3px 10px", borderRadius: 2 }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#ef4444" }} />
                <span style={{ fontSize: 7, fontWeight: 700, color: "#e4e4e7", textTransform: "uppercase", ...mono }}>MEN&apos;S — SEMI-FINALS</span>
              </div>
              <div style={{ position: "absolute", bottom: 10, left: 10, right: 10, display: "flex", gap: 4 }}>
                {["HC", "NR", "DF"].map((init) => (
                  <div key={init} style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(0,0,0,0.7)", padding: "3px 8px", borderRadius: 2 }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 6, fontWeight: 700, color: "#fff" }}>{init}</div>
                    <span style={{ fontSize: 7, color: "#e4e4e7", ...mono }}>-</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 9, color: "#71717a", marginTop: 6 }}>What viewers see in OBS — updates in real-time</div>
          </div>
        )}

        <AutoCursor points={[
          { x: 960, y: 300, frame: 0 },
          { x: 960, y: 300, frame: 30 },
          // Header — point at each element slowly
          { target: "go-live-btn", frame: 50 },
          { target: "go-live-btn", frame: 80 },
          { target: "heat-dropdown", frame: 100 },
          { target: "heat-dropdown", frame: 130 },
          { target: "edit-heats-btn", frame: 140 },
          // Edit Heats modal
          { target: "edit-heats-btn", frame: 155, click: true },
          { target: "edit-heats-modal", frame: 190 },
          { target: "edit-heats-modal", frame: 295 },
          // Overlay controls
          { target: "prod-controls", frame: 320 },
          { target: "prod-controls", frame: 440 },
          // Overlay styles
          { target: "prod-styles", frame: 465 },
          { target: "prod-styles", frame: 590 },
          // Rider cards
          { target: "prod-riders", frame: 620 },
          { target: "prod-riders", frame: 780 },
          // Notifications
          { target: "prod-notifications", frame: 810 },
          { target: "prod-notifications", frame: 950 },
          // OBS preview
          { x: 1200, y: 700, frame: 975 },
          { x: 1200, y: 700, frame: 1070 },
          // Full view
          { x: 960, y: 400, frame: 1080 },
          { x: 960, y: 400, frame: 1090 },
        ]} />
      </ScreenMock>

      <StepLabel text="Go Live starts the broadcast — Go Dark takes the overlay off-air" show={5} hide={60} color="#ef4444" />
      <StepLabel text="The heat dropdown lets you switch between heats while live" show={60} hide={145} color="#3b82f6" />
      <StepLabel text="Edit Heats opens a modal to view divisions, heats, and attempt counts" show={150} hide={295} color="#a855f7" />
      <StepLabel text="Show/Hide toggles control what appears on the OBS overlay" show={300} hide={445} color="#3b82f6" />
      <StepLabel text="Overlay Style changes the visual theme of the broadcast overlay" show={450} hide={595} color="#a855f7" />
      <StepLabel text="Rider cards show scores, attempt progress, Focus and Scores buttons" show={600} hide={785} color="#22c55e" />
      <StepLabel text="Notifications appear live when judges submit or update scores" show={795} hide={955} color="#22c55e" />
      <StepLabel text="The OBS overlay updates in real-time — this is what viewers see" show={965} hide={1075} color="#a855f7" />
      <StepLabel text="That's the full Producer interface" show={1078} hide={1090} color="#22c55e" />
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>You know the interface — next up: going live</div>
    </AbsoluteFill>
  );
}

/* ── Helpers ── */

function glow(active: boolean): React.CSSProperties {
  return active
    ? { boxShadow: "0 0 0 2px #3b82f6, 0 0 15px rgba(59,130,246,0.25)", borderColor: "#3b82f6" }
    : {};
}

/* ── Data ── */

const RIDERS = [
  {
    name: "Harley Clifford", nick: "The King", currentAttempt: 2, final: 8.5 as number | null,
    attempts: [8.5, 7.9, null] as (number | null)[],
    judgeNames: ["Sarah", "Mike", "Lily"],
    judgeScores: [[8.0, 7.5, null], [9.0, 8.5, null], [8.5, 7.7, null]] as (number | null)[][],
  },
  {
    name: "Nic Rapa", nick: "Raptor", currentAttempt: 2, final: 7.2 as number | null,
    attempts: [7.2, 6.8, null] as (number | null)[],
    judgeNames: ["Sarah", "Mike", "Lily"],
    judgeScores: [[7.0, 6.5, null], [7.5, 7.0, null], [7.1, 6.9, null]] as (number | null)[][],
  },
  {
    name: "Dallas Friday", nick: "D-Fri", currentAttempt: 1, final: null as number | null,
    attempts: [null, null, null] as (number | null)[],
    judgeNames: ["Sarah", "Mike", "Lily"],
    judgeScores: [[null, null, null], [null, null, null], [null, null, null]] as (number | null)[][],
  },
  {
    name: "Amber Wing", nick: "Wings", currentAttempt: 2, final: 9.0 as number | null,
    attempts: [9.0, 8.7, null] as (number | null)[],
    judgeNames: ["Sarah", "Mike", "Lily"],
    judgeScores: [[9.2, 8.5, null], [8.8, 9.0, null], [9.0, 8.6, null]] as (number | null)[][],
  },
  {
    name: "Meagan Ethell", nick: "Mega", currentAttempt: 1, final: 8.0 as number | null,
    attempts: [8.0, null, null] as (number | null)[],
    judgeNames: ["Sarah", "Mike", "Lily"],
    judgeScores: [[8.2, null, null], [7.8, null, null], [8.0, null, null]] as (number | null)[][],
  },
  {
    name: "Cory Teunissen", nick: "CT", currentAttempt: 3, final: 8.1 as number | null,
    attempts: [7.5, 8.1, 7.0] as (number | null)[],
    judgeNames: ["Sarah", "Mike", "Lily"],
    judgeScores: [[7.0, 8.0, 6.5], [8.0, 8.5, 7.5], [7.5, 7.8, 7.0]] as (number | null)[][],
  },
];

/* ── Styles ── */

const shell: React.CSSProperties = { width: "100%", height: "100%", background: "#0a0a0b", fontFamily: "'Inter', sans-serif", color: "#e4e4e7", display: "flex", flexDirection: "column", position: "relative" };
const topbar: React.CSSProperties = { height: 42, background: "#111113", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 14, letterSpacing: 2 };
const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };
const M = "'JetBrains Mono', monospace";
const navLink: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717a" };
const badge: React.CSSProperties = { display: "inline-flex", padding: "2px 8px", fontSize: 10, fontWeight: 600, textTransform: "capitalize", color: "#fff" };
const statusBadge: React.CSSProperties = { display: "inline-flex", padding: "2px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "'JetBrains Mono', monospace" };
const cardStyle: React.CSSProperties = { background: "#111113", border: "1px solid #2a2a32", padding: 16 };
